import { useEffect, useState } from "react";

export type ThemePreference = "system" | "light" | "dark";

const THEME_STORAGE_KEY = "juicytraffic.theme";
const DARK_MODE_QUERY = "(prefers-color-scheme: dark)";

function isThemePreference(value: string | null): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function getThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function applyTheme(preference: ThemePreference) {
  const mode = preference === "system"
    ? window.matchMedia(DARK_MODE_QUERY).matches ? "dark" : "light"
    : preference;

  document.documentElement.dataset.mode = mode;
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.style.colorScheme = mode;
}

export function initializeTheme() {
  applyTheme(getThemePreference());
}

export function useThemePreference() {
  const [preference, setPreferenceState] = useState<ThemePreference>(getThemePreference);

  useEffect(() => {
    const media = window.matchMedia(DARK_MODE_QUERY);
    const updateSystemTheme = () => {
      if (getThemePreference() === "system") applyTheme("system");
    };

    media.addEventListener("change", updateSystemTheme);
    return () => media.removeEventListener("change", updateSystemTheme);
  }, []);

  const setPreference = (next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // The selected theme still applies for this page when storage is unavailable.
    }
    applyTheme(next);
  };

  return [preference, setPreference] as const;
}
